import React, { useState, useEffect } from 'react';
import { ShoppingCart, Instagram, Facebook, Menu, X, Search, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { tours as mockTours } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { useTours } from './ToursSection';

const Header = () => {
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const { tours: apiTours } = useTours();
  const tours = apiTours && apiTours.length > 0 ? apiTours : mockTours;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = tours.filter(tour => 
        tour.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tour.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tour.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tour.tag?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, tours]);

  const handleTourClick = (tourId) => {
    navigate(`/tour/${tourId}`);
    setSearchOpen(false);
    setSearchQuery('');
    setMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        {/* Top Bar */}
        <div className="border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            {/* Logo + Language Toggle */}
            <div className="flex items-center gap-4">
              <a 
                href="/" 
                onClick={(e) => { e.preventDefault(); navigate('/'); }}
                className="flex-shrink-0"
              >
                <img
                  src="/logo.jpg"
                  alt="MR Excursion Punta Cana"
                  className="h-10 w-10 rounded-full object-cover border-2 border-teal-500"
                />
              </a>
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 hover:bg-teal-100 transition-colors"
                data-testid="language-toggle"
              >
                <Globe size={16} className="text-teal-600" />
                <span className="text-sm font-medium text-teal-600">
                  {language === 'es' ? 'ES' : 'EN'}
                </span>
                <span className="text-xs text-gray-500">
                  → {language === 'es' ? 'English' : 'Español'}
                </span>
              </button>
            </div>

            {/* Right Links */}
            <div className="flex items-center gap-4">
              <a 
                href="#contacto" 
                onClick={(e) => { e.preventDefault(); scrollToSection('contacto'); }}
                className="text-sm text-gray-600 hover:text-teal-500 transition-colors hidden sm:block"
              >
                {t('contacto')}
              </a>
              <div className="flex items-center gap-2 ml-2">
                <a 
                  href="https://www.instagram.com/mr.excursion?utm_source=qr&igsh=MTYyYW5sNHo5bWFldA==" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-teal-500 transition-colors"
                >
                  <Instagram size={18} />
                </a>
                <a 
                  href="https://www.facebook.com/mrexcursionespuntacana" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-teal-500 transition-colors"
                >
                  <Facebook size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Search Bar - Desktop */}
            <div className="hidden md:flex flex-1 max-w-md">
              <div className="relative w-full">
                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchOpen(true)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-full focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 text-sm"
                  data-testid="search-input"
                />
                {/* Search Results Dropdown */}
                {searchOpen && searchQuery && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-100 max-h-80 overflow-y-auto z-50">
                    {searchResults.length > 0 ? (
                      searchResults.map(tour => (
                        <button
                          key={tour.id}
                          onClick={() => handleTourClick(tour.id)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                        >
                          <img src={tour.image} alt={tour.title} className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{tour.title}</p>
                            <p className="text-teal-500 text-sm">${tour.salePrice} {t('from')}</p>
                          </div>
                        </button>
                      ))
                    ) : (
                      <p className="p-4 text-gray-500 text-center text-sm">{t('noResults')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              <button
                onClick={() => { navigate('/'); scrollToSection('inicio'); }}
                className="text-sm font-medium text-gray-700 hover:text-teal-500 transition-colors"
              >
                {t('inicio')}
              </button>
              <button
                onClick={() => scrollToSection('excursiones')}
                className="text-sm font-medium text-gray-700 hover:text-teal-500 transition-colors"
              >
                {t('excursiones')}
              </button>
              <button
                onClick={() => scrollToSection('contacto')}
                className="text-sm font-medium text-gray-700 hover:text-teal-500 transition-colors"
              >
                {t('contacto')}
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="text-sm font-medium text-gray-500 hover:text-teal-500 transition-colors"
              >
                Admin
              </button>
            </nav>

            {/* Icons */}
            <div className="flex items-center gap-3">
              {/* Search Icon - Mobile */}
              <button 
                className="md:hidden text-gray-700 hover:text-teal-500 transition-colors"
                onClick={() => setSearchOpen(!searchOpen)}
              >
                <Search size={22} />
              </button>
              
              {/* Cart */}
              <button 
                className="text-gray-700 hover:text-teal-500 transition-colors relative"
                onClick={() => setCartOpen(true)}
                data-testid="cart-button"
              >
                <ShoppingCart size={22} />
              </button>
              
              {/* Mobile Menu Button */}
              <button
                className="lg:hidden text-gray-700"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Search */}
          {searchOpen && (
            <div className="md:hidden pt-3">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-full focus:outline-none focus:border-teal-500 text-sm"
                  autoFocus
                />
              </div>
              {searchQuery && (
                <div className="mt-2 bg-white rounded-lg shadow-lg border border-gray-100 max-h-60 overflow-y-auto">
                  {searchResults.length > 0 ? (
                    searchResults.map(tour => (
                      <button
                        key={tour.id}
                        onClick={() => handleTourClick(tour.id)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <img src={tour.image} alt={tour.title} className="w-10 h-10 rounded-lg object-cover" />
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{tour.title}</p>
                          <p className="text-teal-500 text-xs">${tour.salePrice}</p>
                        </div>
                      </button>
                    ))
                  ) : (
                    <p className="p-4 text-gray-500 text-center text-sm">{t('noResults')}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-0 right-0 w-80 max-w-full h-full bg-white shadow-xl overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-bold text-lg text-gray-800">{t('allTours')}</h2>
              <button onClick={() => setMobileMenuOpen(false)} className="text-gray-500">
                <X size={24} />
              </button>
            </div>
            
            {/* Navigation Links */}
            <div className="p-4 border-b">
              <button
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                className="block w-full text-left py-2 text-gray-700 hover:text-teal-500 font-medium"
              >
                {t('inicio')}
              </button>
              <button
                onClick={() => scrollToSection('excursiones')}
                className="block w-full text-left py-2 text-gray-700 hover:text-teal-500 font-medium"
              >
                {t('excursiones')}
              </button>
              <button
                onClick={() => scrollToSection('contacto')}
                className="block w-full text-left py-2 text-gray-700 hover:text-teal-500 font-medium"
              >
                {t('contacto')}
              </button>
              <button
                onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                className="block w-full text-left py-2 text-gray-500 hover:text-teal-500 font-medium"
              >
                Admin
              </button>
            </div>

            {/* All Tours List */}
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{t('excursiones')}</h3>
              <div className="space-y-2">
                {tours.map(tour => (
                  <button
                    key={tour.id}
                    onClick={() => handleTourClick(tour.id)}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  >
                    <img src={tour.image} alt={tour.title} className="w-12 h-12 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-sm truncate">{tour.title}</p>
                      <p className="text-teal-500 text-sm font-bold">${tour.salePrice}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart Sidebar */}
      {cartOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCartOpen(false)} />
          <div className="absolute top-0 right-0 w-96 max-w-full h-full bg-white shadow-xl">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-bold text-lg text-gray-800">{t('cart')}</h2>
              <button onClick={() => setCartOpen(false)} className="text-gray-500">
                <X size={24} />
              </button>
            </div>
            <div className="p-8 text-center">
              <ShoppingCart size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">{t('cartEmpty')}</p>
              <p className="text-sm text-gray-400 mt-2">{t('cartNote')}</p>
              <button
                onClick={() => { setCartOpen(false); scrollToSection('excursiones'); }}
                className="mt-4 px-6 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors"
              >
                {t('viewAll')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close search */}
      {searchOpen && searchQuery && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
        />
      )}
    </>
  );
};

export default Header;
